import type { AxiosInstance } from "axios";
import type { TranslateFn, U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import type { U2000SearchPayload, U2000SearchParameter } from "../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GenerateUrlUUID } from "../u2000Core/tools/U2000UrlHelpers";

const LOGICALUNIT_URL = "U2102";

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
  LOADLOGICALUNIT: () => string;
  EXPORT_EXCEL: string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
  LOADLOGICALUNIT: () => `${LOGICALUNIT_URL}/loadLogicalUnitActions`,
  EXPORT_EXCEL: `${LOGICALUNIT_URL}/exportExcel`,
};

export interface U2102Role {
  id: string;
  idChecked: boolean;
  roleCode: string;
  roleDescription: string;
  actions: U2102RoleLogicalUnit[];
}

export interface U2102RoleLogicalUnit {
  id: string;
  logicalUnitCode: string;
  logicalUnitDescription: string;
  actions: string[];
}

export interface U2102LogicalUnit extends Record<string, unknown> {
  id: string;
  logicalUnitCode: string;
  logicalUnitDescription: string;
  logicalUnitActions: U2102LogicalUnitAction[];
}

export interface U2102LogicalUnitAction {
  id: string;
  idChecked: boolean;
  logicalUnitActionCode: string;
  logicalUnitActionDescription: string;
}

export const emptyRole: U2102Role = {
  id: "",
  idChecked: false,
  roleCode: "",
  roleDescription: "",
  actions: [],
};

export const emptyLogicalUnit: U2102LogicalUnit = {
  id: "",
  logicalUnitCode: "",
  logicalUnitDescription: "",
  logicalUnitActions: [],
};

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, data: U2102Role) => {
  return axios.post(LogicalUnitEndpoints.CREATE, data);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, data: U2102Role) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), data);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, data?: U2102Role) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), data);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

export const doLoadLogicalUnitActions = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.LOADLOGICALUNIT());
};

// API Calls - Export Excel
export const doExportExcel = (axios: AxiosInstance, filter: U2000SearchParameter, lang?: "fr" | "en") => {
  return axios.post(LogicalUnitEndpoints.EXPORT_EXCEL, { filter, lang });
};

export function luHandleAppiError(_t: TranslateFn, _NS: string, _err: any, _errorResult: U2000ApiErrorResult) {
  // Gestion des erreurs spécifiques à l'unité logique U2102
}
